import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, UserPlus } from 'lucide-react';
import CareLayout from './CareLayout';
import { care, fail, can, openFile, rm } from './careApi';
import { Badge, Btn, Card, Input, PageHeader, Select, Table, Loading } from './careUi';
import { ask } from './AskDialog';

export function CareAdmissionForms() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ status: '', form_type: '' });
  useEffect(() => { care.get('/forms', { params: { status: f.status || undefined, form_type: f.form_type || undefined } }).then((r) => setRows(r.data)).catch(fail); }, [f]);
  return (
    <CareLayout>
      <PageHeader title="Admission Forms" sub="Status of the mandatory forms and checklist for every resident." />
      <Card>
        <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-slate-100">
          <Select value={f.form_type} onChange={(e) => setF({ ...f, form_type: e.target.value })} className="sm:w-64" testid="forms-type-filter"
            options={[{ value: '', label: 'All forms' }, { value: 'admission', label: 'Resident Admission Form' }, { value: 'pdpa', label: 'PDPA Notice and Consent' },
              { value: 'contract', label: "Resident's Contract" }, { value: 'checklist', label: 'Resident Admission Checklist' }]} />
          <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="sm:w-48" testid="forms-status-filter"
            options={[{ value: '', label: 'All statuses' }, 'Pending', 'In Progress', 'Completed', 'Approved']} />
        </div>
        {!rows ? <Loading /> : <Table testid="forms-table" rows={rows} onRow={(x) => nav(`/care/residents/${x.resident.id}`)} cols={[
          { k: 'r', h: 'Resident', r: (x) => <>{x.resident.name}<span className="block text-[11px] text-slate-400">{x.resident.resident_id}</span></> },
          { k: 'label', h: 'Form' }, { k: 'status', h: 'Status', r: (x) => <Badge s={x.status} /> },
          { k: 'updated_at', h: 'Last updated', r: (x) => (x.updated_at || '').slice(0, 16).replace('T', ' ') }]} />}
      </Card>
    </CareLayout>
  );
}

const REPORTS = [
  { k: 'residents', l: 'Resident listing', roles: ['admin', 'staff'] }, { k: 'forms', l: 'Admission forms (completed & pending)', roles: ['admin', 'staff'] },
  { k: 'invoices', l: 'Monthly invoices', roles: ['admin', 'finance'] }, { k: 'payments', l: 'Payments collected', roles: ['admin', 'finance'] },
  { k: 'outstanding', l: 'Outstanding invoices', roles: ['admin', 'finance'] }, { k: 'overdue', l: 'Overdue payments', roles: ['admin', 'finance'] },
];
const MONEY = ['total', 'amount_paid', 'balance', 'amount', 'monthly_amount'];

export function CareReports() {
  const list = REPORTS.filter((r) => can(...r.roles));
  const [kind, setKind] = useState(list[0]?.k);
  const [f, setF] = useState({ date_from: '', date_to: '' });
  const [data, setData] = useState(null);
  const params = { date_from: f.date_from || undefined, date_to: f.date_to || undefined };
  useEffect(() => { setData(null); care.get(`/reports/${kind}`, { params }).then((r) => setData(r.data)).catch(fail); }, [kind, f]); // eslint-disable-line react-hooks/exhaustive-deps
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  return (
    <CareLayout>
      <PageHeader title="Reports" sub="Filter by date and export to CSV (opens in Excel).">
        <Btn variant="ghost" onClick={() => openFile(`/reports/${kind}/csv${qs ? `?${qs}` : ''}`, true, `care-${kind}-report.csv`)} data-testid="report-export-csv"><Download className="w-4 h-4" />Export CSV</Btn>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        {list.map((r) => (
          <button key={r.k} onClick={() => setKind(r.k)} data-testid={`report-${r.k}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${kind === r.k ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>{r.l}</button>
        ))}
      </div>
      <div className="flex gap-3 mb-4">
        <Input type="date" label="From" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} testid="report-from" />
        <Input type="date" label="To" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} testid="report-to" />
      </div>
      <Card>{!data ? <Loading /> : <>
        <p className="px-4 pt-3 text-xs text-slate-500" data-testid="report-count">{data.rows.length} record(s)</p>
        <Table testid="report-table" rows={data.rows} cols={data.columns.map((c) => ({ k: c.key, h: c.label, right: MONEY.includes(c.key),
          r: (x) => (MONEY.includes(c.key) ? rm(x[c.key]) : c.key === 'status' ? <Badge s={x.status} /> : String(x[c.key] ?? '').slice(0, 40)) }))} />
      </>}</Card>
    </CareLayout>
  );
}

const ROLE_OPTS = [{ value: 'admin', label: 'Admin' }, { value: 'staff', label: 'Staff' }, { value: 'finance', label: 'Finance' }];

export function CareUsers() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: '', email: '', role: 'staff', password: '' });
  const [busy, setBusy] = useState(false);
  const load = () => care.get('/users').then((r) => setRows(r.data)).catch(fail);
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await care.post('/users', f); toast.success('User created'); setF({ name: '', email: '', role: 'staff', password: '' }); load(); }
    catch (err) { fail(err); } finally { setBusy(false); }
  };
  const update = async (u, body, msg) => { try { await care.put(`/users/${u.id}`, body); toast.success(msg); load(); } catch (err) { fail(err); } };
  const resetPw = async (u) => { const p = await ask({ title: `Set password for ${u.email}`, label: 'New password (min 8 characters)', type: 'password', required: true, minLength: 8, confirm: 'Save password' }); if (p) update(u, { password: p }, 'Password updated'); };
  return (
    <CareLayout>
      <PageHeader title="Users" sub="Admin: everything. Staff: residents and forms. Finance: billing, payments and receipts." />
      <Card className="p-5 mb-6">
        <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="user-create-form">
          <Input label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="user-name" />
          <Input label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="user-email" />
          <Select label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} options={ROLE_OPTS} testid="user-role" />
          <Input label="Temporary password" type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="user-password" />
          <Btn type="submit" loading={busy} data-testid="user-create-submit"><UserPlus className="w-4 h-4" />Create user</Btn>
        </form>
      </Card>
      <Card>{!rows ? <Loading /> : <Table testid="users-table" rows={rows} cols={[
        { k: 'name', h: 'Name' }, { k: 'email', h: 'Email' },
        { k: 'role', h: 'Role', r: (u) => <select value={u.role} onChange={(e) => update(u, { role: e.target.value }, 'Role updated')} data-testid={`user-role-${u.email}`} className="border border-slate-300 rounded px-2 py-1 text-sm">{ROLE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select> },
        { k: 'active', h: 'Status', r: (u) => <Badge s={u.active ? 'Active' : 'Discharged'} /> },
        { k: 'last_login_at', h: 'Last login', r: (u) => (u.last_login_at || '').slice(0, 16).replace('T', ' ') },
        { k: 'a', h: '', right: true, r: (u) => (
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" className="!py-1" onClick={() => resetPw(u)} data-testid={`user-reset-${u.email}`}>Set password</Btn>
            <Btn variant={u.active ? 'ghost' : 'primary'} className="!py-1" onClick={() => update(u, { active: !u.active }, u.active ? 'User deactivated' : 'User activated')} data-testid={`user-toggle-${u.email}`}>{u.active ? 'Deactivate' : 'Activate'}</Btn>
          </div>) }]} />}</Card>
    </CareLayout>
  );
}

const SETTINGS = [
  ['company_name', 'Nursing home name (shown on forms)'], ['legal_name', 'Company legal name'], ['registration_no', 'Company registration no.'],
  ['address', 'Address'], ['phone', 'Phone'], ['email', 'Email'], ['bank_details', 'Bank details for invoices'],
  ['provider_company_no', "Contract: Provider company no. (blank = registration no.)"], ['provider_registered_office', 'Contract: Provider registered office (blank = address)'],
  ['resident_id_prefix', 'Resident ID prefix'], ['invoice_due_days', 'Default invoice due (days)'],
];

export function CareSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { care.get('/settings').then((r) => setS(r.data)).catch(fail); }, []);
  const save = async () => {
    setBusy(true);
    try { const { data } = await care.put('/settings', s); setS(data); toast.success('Settings saved'); } catch (e) { fail(e); } finally { setBusy(false); }
  };
  return (
    <CareLayout>
      <PageHeader title="Settings" sub="Company details used on forms, invoices and receipts." />
      {s && s.org_slug && (
        <Card className="p-5 max-w-3xl mb-4" data-testid="settings-register-link">
          <p className="text-xs text-slate-500">Online registration link for families ({s.org_name})</p>
          <p className="text-sm font-mono text-teal-800 break-all mt-1" data-testid="settings-register-url">{`${window.location.origin}/care/register/${s.org_slug}`}</p>
        </Card>
      )}
      {!s ? <Loading /> : (
        <Card className="p-5 max-w-3xl">
          <div className="grid sm:grid-cols-2 gap-4">
            {SETTINGS.map(([k, l]) => <Input key={k} label={l} value={s[k] ?? ''} onChange={(e) => setS({ ...s, [k]: e.target.value })} testid={`setting-${k}`} className={['address', 'bank_details', 'provider_registered_office'].includes(k) ? 'sm:col-span-2' : ''} />)}
          </div>
          <p className="text-xs text-slate-400 mt-4">Online payments: Stripe (test mode in Preview). Currency {rm(0).split(' ')[0]}.</p>
          <Btn className="mt-4" loading={busy} onClick={save} data-testid="settings-save">Save settings</Btn>
        </Card>
      )}
    </CareLayout>
  );
}
